import React, { useState } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  Container,
  InputLabel,
  Stack,
  TextField,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Modal,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import InputMask from 'react-input-mask';
import { useForm, Controller } from 'react-hook-form';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { api as dealerApi } from '../store/domains/stock';
import WorkHours from '../components/WorkingHours/WorkingHours';
import Location from '../components/Location/Location';
import { DOCUMENT_SERIES } from '../constants';

const SUCCESS_MODAL_STYLE = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: '13px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxShadow: 24,
  p: '20px',
};

function splitDocumentNumber(idCardNumber) {
  const matchedSerie = Object.keys(DOCUMENT_SERIES).find((serie) => idCardNumber.startsWith(serie));
  return {
    serie: matchedSerie,
    documentNumber: matchedSerie ? idCardNumber.slice(DOCUMENT_SERIES[matchedSerie]) : idCardNumber,
  };
}

function DealerCreate() {
  const [selectedHub, setSelectedHub] = useState({ hubId: '', hubTitle: '' });
  const [successModal, setSuccessModal] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(false);

  const { data: hubList } = dealerApi.useGetHubList();
  const { data: countryList } = dealerApi.useGetCountries();

  const [getUserInfo, { isLoading: userInfoLoading }] = dealerApi.useGetUserInfo();
  const [createDealer, { isLoading: createLoading }] = dealerApi.useCreateDealer();

  const [verifiedUser, setVerifiedUser] = useState(null);

  const {
    control,
    handleSubmit: handleVerifySubmit,
    reset: resetVerifyForm,
    getValues: getVerifyValues,
  } = useForm({
    defaultValues: {
      idCardNumber: '',
      idCardPin: '',
      countryCode: '',
      documentType: 'IDENTITY_CARD',
    },
  });

  const {
    control: finalControl,
    handleSubmit: handleFinalSubmit,
    reset: resetFinalForm,
  } = useForm({
    defaultValues: {
      title: '',
      tradePointTitle: '',
      email: '',
      contactNumber: '',
      address: null,
      workingHours: [{ weekdays: [], startTime: '', endTime: '' }],
    },
  });

  const handleClose = () => {
    setSuccessModal(false);
    resetVerifyForm();
    resetFinalForm();
    setIsUserVerified(false);
    setVerifiedUser(null);
    setSelectedHub({ hubId: '', hubTitle: '' });
  };

  const handleChangeClick = (event) => {
    event.preventDefault();
    setIsUserVerified(false);
    setVerifiedUser(null);
  };

  const handleHubChange = (event) => {
    const { hubTitle } = hubList.find((item) => item.hubId === event.target.value) ?? {};
    setSelectedHub({ hubId: event.target.value, hubTitle: hubTitle ?? '' });
  };

  const onVerifySubmit = async (data) => {
    try {
      const userInfo = await getUserInfo(data).unwrap();
      setVerifiedUser(userInfo);
      setIsUserVerified(true);
    } catch {
      console.log('');
    }
  };

  const onFinalSubmit = async (data) => {
    const idCardNumber = getVerifyValues('idCardNumber');
    const { serie, documentNumber } = splitDocumentNumber(idCardNumber);

    const payload = {
      title: data.title,
      ...selectedHub,
      contactNumbers: [`+994${data.contactNumber.replace(/\s/g, '')}`],
      emails: [data.email],
      address: data.address,
      workingHours: data.workingHours,
      tradePointTitle: data.tradePointTitle,
      users: [
        {
          pin: verifiedUser.pinCode,
          name: verifiedUser.name,
          fatherName: verifiedUser.fatherName,
          surname: verifiedUser.surname,
          dateOfBirth: verifiedUser.birthDate,
          placeOfBirth: verifiedUser.birthPlace,
          nationality: verifiedUser.nationality,
          document: {
            documentNumber,
            serie,
          },
        },
      ],
    };

    try {
      await createDealer(payload).unwrap();
      setSuccessModal(true);
    } catch {
      console.log('');
    }
  };

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Stack direction="row" gap={2} component="div">
        <Controller
          name="idCardNumber"
          control={control}
          rules={{ required: 'ID card number is required' }}
          render={({ field, formState: { errors } }) => (
            <Box sx={{ width: '100%' }}>
              <TextField {...field} disabled={isUserVerified} label="ID card number" sx={{ width: '100%' }} />
              {errors.idCardNumber && <FormHelperText error>{errors.idCardNumber.message}</FormHelperText>}
            </Box>
          )}
        />

        <Controller
          name="idCardPin"
          control={control}
          rules={{ required: 'ID card PIN is required' }}
          render={({ field, formState: { errors } }) => (
            <Box sx={{ width: '100%' }}>
              <TextField {...field} disabled={isUserVerified} label="ID card PIN" sx={{ width: '100%' }} />
              {errors.idCardPin && <FormHelperText error>{errors.idCardPin.message}</FormHelperText>}
            </Box>
          )}
        />

        <Controller
          name="countryCode"
          control={control}
          rules={{ required: 'Country is required' }}
          render={({ field, formState: { errors } }) => (
            <FormControl fullWidth>
              <InputLabel id="countryCode-label">Country</InputLabel>
              <Select {...field} labelId="countryCode-label" label="Country" disabled={isUserVerified}>
                {countryList?.data?.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.countryCode && <FormHelperText error>{errors.countryCode.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Controller
          name="documentType"
          control={control}
          rules={{ required: 'Document type is required' }}
          render={({ field, formState: { errors } }) => (
            <FormControl fullWidth>
              <InputLabel id="documentType-label">Document Type</InputLabel>
              <Select {...field} labelId="documentType-label" label="Document Type" disabled={isUserVerified}>
                <MenuItem value="IDENTITY_CARD">Identity Card</MenuItem>
              </Select>
              {errors.documentType && <FormHelperText error>{errors.documentType.message}</FormHelperText>}
            </FormControl>
          )}
        />

        {isUserVerified ? (
          <Button onClick={handleChangeClick} sx={{ width: '200px', maxHeight: '56px' }} variant="contained">
            Change
          </Button>
        ) : (
          <Button
            onClick={handleVerifySubmit(onVerifySubmit)}
            sx={{ width: '200px', maxHeight: '56px' }}
            variant="contained"
            disabled={userInfoLoading}
          >
            {userInfoLoading ? 'Processing…' : 'Verify'}
          </Button>
        )}
      </Stack>

      {isUserVerified && verifiedUser && (
        <Card sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '200px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: '1 0 auto' }}>
              <Typography component="div" variant="h5" sx={{ mb: '20px' }}>
                {`${verifiedUser.surname} ${verifiedUser.name} ${verifiedUser.fatherName}`}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" component="div">
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    m: 0,
                    p: 0,
                  }}
                >
                  <li>
                    <Box component="span" sx={{ color: 'text.primary' }}>
                      Serial number:
                    </Box>{' '}
                    {verifiedUser.serialNumber}
                  </li>
                  <li>
                    <Box component="span" sx={{ color: 'text.primary' }}>
                      Date of birth:
                    </Box>{' '}
                    {verifiedUser.birthDate}
                  </li>
                </Box>
              </Typography>
            </CardContent>
          </Box>
        </Card>
      )}

      {isUserVerified && verifiedUser ? (
        <Box
          component="form"
          onSubmit={handleFinalSubmit(onFinalSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
        >
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="hub-label">Hub</InputLabel>
              <Select labelId="hub-label" label="Hub" value={selectedHub.hubId} onChange={handleHubChange}>
                {hubList?.map(({ hubId, title }) => (
                  <MenuItem key={hubId} value={hubId}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Controller
              name="title"
              control={finalControl}
              rules={{ required: 'Title is required' }}
              render={({ field, formState: { errors } }) => (
                <Box sx={{ width: '100%' }}>
                  <TextField {...field} fullWidth label="Title" variant="outlined" />
                  {errors.title && <FormHelperText error>{errors.title.message}</FormHelperText>}
                </Box>
              )}
            />

            <Controller
              name="tradePointTitle"
              control={finalControl}
              rules={{ required: 'Trade point title is required' }}
              render={({ field, formState: { errors } }) => (
                <Box sx={{ width: '100%' }}>
                  <TextField {...field} fullWidth label="Trade point title" variant="outlined" />
                  {errors.tradePointTitle && <FormHelperText error>{errors.tradePointTitle.message}</FormHelperText>}
                </Box>
              )}
            />

            <Controller
              name="email"
              control={finalControl}
              rules={{ required: 'Email is required' }}
              render={({ field, formState: { errors } }) => (
                <Box sx={{ width: '100%' }}>
                  <TextField {...field} type="email" fullWidth label="Email" variant="outlined" />
                  {errors.email && <FormHelperText error>{errors.email.message}</FormHelperText>}
                </Box>
              )}
            />

            <Controller
              name="contactNumber"
              control={finalControl}
              rules={{ required: 'Contact number is required' }}
              render={({ field, formState: { errors } }) => (
                <Box sx={{ width: '100%' }}>
                  <InputMask {...field} mask="99 999 99 99" maskChar="">
                    <TextField
                      sx={{ width: '100%' }}
                      label="Contact number"
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+994</InputAdornment>,
                      }}
                    />
                  </InputMask>
                  {errors.contactNumber && <FormHelperText error>{errors.contactNumber.message}</FormHelperText>}
                </Box>
              )}
            />
          </Stack>

          <WorkHours control={finalControl} />
          <Location control={finalControl} />

          <Button sx={{ my: 5 }} type="submit" variant="contained" disabled={createLoading}>
            {createLoading ? 'Processing…' : 'Create'}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            minHeight: '400px',
            borderRadius: '13px',
            border: '1px solid #e7eaed',
          }}
        >
          <ErrorOutlineIcon fontSize="large" />
          <Typography variant="h6" component="h2" mt={1}>
            To continue, please enter valid identity data above
          </Typography>
        </Box>
      )}

      <Modal
        open={successModal}
        onClose={handleClose}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box sx={SUCCESS_MODAL_STYLE}>
          <Typography id="success-modal-title" variant="h6" component="h2">
            Dealer created successfully
          </Typography>
          <Typography id="success-modal-description" sx={{ mt: 2 }}>
            To leave, press Close.
          </Typography>
          <Button onClick={handleClose} sx={{ mt: '5px' }} variant="contained">
            Close
          </Button>
        </Box>
      </Modal>
    </Container>
  );
}

export default DealerCreate;
